// Fetch + decode Shortbread (MVT) tiles from VersaTiles, with a small LRU of decoded native
// tiles. Shared by the render worker and the main-thread fallback (each gets its own instance
// of the module-level cache, since they run in separate realms).

import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';
import { LruCache } from './tiles';
import type { VTFeature, VTLayers } from './vectorStyle';

export const VERSATILES_URL = 'https://tiles.versatiles.org/tiles/osm/{z}/{x}/{y}';

export function tileUrl(z: number, x: number, y: number): string {
  return `https://tiles.versatiles.org/tiles/osm/${z}/${x}/${y}`;
}

export interface DecodedTile {
  layers: VTLayers;
  extent: number;
}

const EMPTY_TILE: DecodedTile = { layers: {}, extent: 4096 };
const decodeCache = new LruCache<DecodedTile>(32);

/** Fetch and decode a native tile (z, x, y already wrapped/clamped by the caller), with caching. */
export async function fetchAndDecode(z: number, x: number, y: number, signal: AbortSignal): Promise<DecodedTile> {
  const key = `${z}/${x}/${y}`;
  const cached = decodeCache.get(key);
  if (cached) return cached;
  const res = await fetch(tileUrl(z, x, y), { mode: 'cors', credentials: 'omit', signal });
  if (!res.ok) {
    if (res.status === 204 || res.status === 404) {
      decodeCache.set(key, EMPTY_TILE);
      return EMPTY_TILE;
    }
    throw new Error(`HTTP ${res.status}`);
  }
  const buf = await res.arrayBuffer();
  if (buf.byteLength === 0) {
    decodeCache.set(key, EMPTY_TILE);
    return EMPTY_TILE;
  }
  const vt = new VectorTile(new PbfReader(buf));
  const layers: VTLayers = {};
  let extent = 4096;
  for (const name of Object.keys(vt.layers)) {
    const layer = vt.layers[name];
    if (layer.extent) extent = layer.extent;
    const feats: VTFeature[] = [];
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      feats.push({ type: f.type, properties: f.properties, geometry: f.loadGeometry() });
    }
    layers[name] = feats;
  }
  const decoded: DecodedTile = { layers, extent };
  decodeCache.set(key, decoded);
  return decoded;
}

/** Sub-square of the native (maxNativeZoom) parent tile that a virtual overzoomed tile covers. */
export interface OverzoomCrop {
  nz: number;
  nx: number;
  ny: number;
  /** Fraction of the parent tile's coordinate space, in [0, 1]. */
  fx0: number;
  fy0: number;
  fsize: number;
}

export function overzoomCrop(z: number, x: number, y: number, maxNativeZoom: number): OverzoomCrop {
  if (z <= maxNativeZoom) return { nz: z, nx: x, ny: y, fx0: 0, fy0: 0, fsize: 1 };
  const d = z - maxNativeZoom;
  const n = 1 << d;
  const nx = x >> d;
  const ny = y >> d;
  const fsize = 1 / n;
  const fx0 = (x - (nx << d)) * fsize;
  const fy0 = (y - (ny << d)) * fsize;
  return { nz: maxNativeZoom, nx, ny, fx0, fy0, fsize };
}
